import type { EmittableLogLevel } from './logging.types';
import { LogLevel } from './logging.types';

/** Numeric severity rank per level, ascending. Silent outranks everything. */
export const LOG_LEVEL_RANK: Readonly<Record<LogLevel, number>> = {
    [LogLevel.Trace]: 0,
    [LogLevel.Debug]: 1,
    [LogLevel.Info]: 2,
    [LogLevel.Notice]: 3,
    [LogLevel.Warn]: 4,
    [LogLevel.Error]: 5,
    [LogLevel.Critical]: 6,
    [LogLevel.Alert]: 7,
    [LogLevel.Emergency]: 8,
    [LogLevel.Silent]: 9,
};

/** Console method used to print each emittable level. */
export type ConsoleMethod = 'debug' | 'error' | 'info' | 'warn';

/** Level → console method. Sub-info noise prints via `console.debug`. */
export const LOG_LEVEL_CONSOLE_METHOD: Readonly<Record<EmittableLogLevel, ConsoleMethod>> = {
    [LogLevel.Trace]: 'debug',
    [LogLevel.Debug]: 'debug',
    [LogLevel.Info]: 'info',
    [LogLevel.Notice]: 'info',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Error]: 'error',
    [LogLevel.Critical]: 'error',
    [LogLevel.Alert]: 'error',
    [LogLevel.Emergency]: 'error',
};

/** Datadog status accepted by the browser-logs SDK (v7 supports full syslog). */
export type DatadogLogStatus = 'alert' | 'critical' | 'debug' | 'emerg' | 'error' | 'info' | 'notice' | 'warn';

/** Level → Datadog status, 1:1 apart from `trace` (→ `debug`; the original level ships as an attribute). */
export const LOG_LEVEL_DATADOG_STATUS: Readonly<Record<EmittableLogLevel, DatadogLogStatus>> = {
    [LogLevel.Trace]: 'debug',
    [LogLevel.Debug]: 'debug',
    [LogLevel.Info]: 'info',
    [LogLevel.Notice]: 'notice',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Error]: 'error',
    [LogLevel.Critical]: 'critical',
    [LogLevel.Alert]: 'alert',
    [LogLevel.Emergency]: 'emerg',
};

/** Accepted spellings that are not enum values themselves. */
const LOG_LEVEL_ALIASES: Readonly<Record<string, LogLevel>> = {
    crit: LogLevel.Critical,
    emerg: LogLevel.Emergency,
    off: LogLevel.Silent,
    warning: LogLevel.Warn,
};

const LOG_LEVEL_VALUES = new Set<string>(Object.values(LogLevel));

/** Whether an entry at `level` passes a minimum-level threshold. */
export function meetsThreshold(level: EmittableLogLevel, threshold: LogLevel): boolean {
    return LOG_LEVEL_RANK[level] >= LOG_LEVEL_RANK[threshold];
}

/** Parse a level string (env var, console input); undefined when unknown. Aliases accepted. */
export function tryParseLogLevel(value: string | undefined): LogLevel | undefined {
    const normalized = value?.trim().toLowerCase() ?? '';

    if (LOG_LEVEL_VALUES.has(normalized)) {
        return normalized as LogLevel;
    }

    return LOG_LEVEL_ALIASES[normalized];
}

/** Parse a level string, returning the fallback when missing or unknown. */
export function parseLogLevel(value: string | undefined, fallback: LogLevel): LogLevel {
    return tryParseLogLevel(value) ?? fallback;
}
